import { createFormatForDescription as cffd } from '../createFormatForDescription'
import PaymentPath, { PathStep } from '../types/util/PaymentPath'

import { Explanation, explainOptional, explainArray } from './Explanation'

/**
 * Stores an explanation for a payment step. Does not explain deprecated values.
 *
 * @property account - An explanation for the account.
 * @property currency - An explanation for the currency.
 * @property issuer - An explanation for the issue.
 * @property type - The type.
 * @property typeHex - The typeHex.
 */
interface PathStepExplanation {
  readonly account?: Explanation<string>
  readonly currency?: Explanation<string>
  readonly issuer?: Explanation<string>
  readonly type?: number
  readonly typeHex?: string
}

/**
 * Creates an explanation for a payment step.
 *
 * @param pathStep - The step to be explained.
 * @returns Returns an explanation of the payment step.
 */
function createPathStepExplanation(pathStep: PathStep): PathStepExplanation {
  const account = explainOptional(
    pathStep.account,
    (theAccount) =>
      `This payment step "ripples" through address ${cffd(
        theAccount,
        'account',
      )}.`,
  )

  const currency = explainOptional(
    pathStep.currency,
    (theCurrency) =>
      `This payment step involves exchanging to currency ${cffd(
        theCurrency,
        'currency',
      )} through an order book.`,
  )

  const issuer = explainOptional(
    pathStep.issuer,
    (theIssuer) =>
      `This path step represents changing currencies. The new issuer of the new currency is ${cffd(
        theIssuer,
        'issuer',
      )}`,
  )

  const { type } = pathStep
  const typeHex = pathStep.type_hex

  return {
    ...(account && { account }),
    ...(currency && { currency }),
    ...(issuer && { issuer }),
    ...(type && { type }),
    ...(typeHex && { typeHex }),
  }
}

/**
 * Stores an explanation for a complete payment path.
 *
 */
type PaymentPathExplanation = Explanation<PathStepExplanation[]>

export { PaymentPathExplanation }

/**
 * Creates an explanation for a complete payment path.
 *
 * @param path - The path to be explained.
 * @returns The explanation of the path.
 */
export function createPaymentPathExplanation(
  path: PaymentPath,
): PaymentPathExplanation {
  return explainArray(
    path,
    createPathStepExplanation,
    'One of the possible payment paths. The path takes the following steps:',
  )
}
